import React, { JSX, useEffect, useId, useMemo, useState } from 'react'
import { useAuth } from 'context/user'
import { useEventsList } from 'context/eventsList'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { confirmDialogKeyDown } from 'components/ConfirmDialog'
import { graphqlRequest } from 'lib/helpers'
import { sortByName } from 'lib/elementOrder'
import { ChatOverlaySummary, EmoteWallSummary, EventDetail, EventSummary, TickerBar, TickerBarSummary } from 'types/api'
import listMyChatOverlaysQuery from 'graphql/listMyChatOverlays.graphql'
import listMyEmoteWallsQuery from 'graphql/listMyEmoteWalls.graphql'
import listMyTickerBarsQuery from 'graphql/listMyTickerBars.graphql'
import getEventQuery from 'graphql/getEvent.graphql'
import copyChatOverlayToEventMutation from 'graphql/copyChatOverlayToEvent.graphql'
import copyEmoteWallToEventMutation from 'graphql/copyEmoteWallToEvent.graphql'
import copyTickerBarToEventMutation from 'graphql/copyTickerBarToEvent.graphql'

export const COPY_DIALOG_COPY = Object.freeze({
  title: 'Add one I already made',
  body: 'This makes a copy in this stream. The copy never stays in sync, and the original is not changed.',
  tabQuick: 'From my Quick Overlays',
  tabStream: 'From another stream I am in',
  useStream: 'Use this stream\'s channels',
  keepMine: 'Keep my channels',
  hint: 'Channels only show chat on the overlay. They never count votes in this stream. YouTube chats are not copied: pick this stream\'s YouTube after copying.',
  button: 'Copy into this stream'
})

export type CopyKind = 'chat' | 'wall' | 'tickerbar'
export type CopyChannels = 'STREAM' | 'MINE'

export const copyKindHasChannels = (kind: CopyKind): boolean => kind !== 'tickerbar'

export const updatedLabelOf = (updatedAt: string | null | undefined): string => {
  const parsed = new Date(updatedAt ?? '')
  return Number.isNaN(parsed.getTime()) ? 'Updated' : `Updated ${parsed.toLocaleDateString()}`
}
type CopySource = 'quick' | 'stream'

export const streamChannelsAvailable = (event: Pick<EventDetail, 'channels' | 'youtubeSourceAttached'>): boolean =>
  (event.channels?.length ?? 0) > 0 || event.youtubeSourceAttached === true

export const defaultCopyChannels = (event: Pick<EventDetail, 'channels' | 'youtubeSourceAttached'>): CopyChannels =>
  streamChannelsAvailable(event) ? 'STREAM' : 'MINE'

export const copySourceStreams = (events: readonly EventSummary[], currentEventId: string): EventSummary[] =>
  sortByName(events.filter((e) => e.eventId !== currentEventId), (e) => e.name, (e) => e.eventId)

interface SourceRow {
  id: string
  name: string
  detail: string
}

const chatRowOf = (c: ChatOverlaySummary): SourceRow => {
  const n = c.twitchChatChannels.length + (c.youtubeChatChannels?.length ?? 0)
  return { id: c.chatId, name: c.name, detail: `${n} channel${n === 1 ? '' : 's'}` }
}

const wallRowOf = (w: EmoteWallSummary): SourceRow => {
  const youtube = (w.youtubeChatChannels?.length ?? 0) > 0
  const n = youtube ? (w.youtubeChatChannels?.length ?? 0) : w.twitchChatChannels.length
  return { id: w.emoteWallId, name: w.name, detail: youtube ? `YouTube · ${n} live chat${n === 1 ? '' : 's'}` : `${n} channel${n === 1 ? '' : 's'}` }
}

const tickerBarRowOf = (t: Pick<TickerBarSummary, 'tickerBarId' | 'name' | 'updatedAt'>): SourceRow =>
  ({ id: t.tickerBarId, name: t.name, detail: updatedLabelOf(t.updatedAt) })

export const copyVariablesFor = ({
  kind,
  eventId,
  fromId,
  fromEventId,
  channels
}: {
  kind: CopyKind
  eventId: string
  fromId: string
  fromEventId: string | null
  channels: CopyChannels
}): Record<string, unknown> => {
  if (kind === 'tickerbar') {
    return { eventId, fromTickerBarId: fromId, ...(fromEventId != null && { fromEventId }) }
  }
  return {
    eventId,
    ...(kind === 'chat' ? { fromChatId: fromId } : { fromEmoteWallId: fromId }),
    ...(fromEventId != null && { fromEventId }),
    channels
  }
}

const tabClass = (on: boolean): string => on
  ? 'rounded bg-white/10 px-3 py-1 text-xs font-semibold text-dm-text-primary ring-1 ring-inset ring-white/20'
  : 'rounded px-3 py-1 text-xs font-medium text-dm-text-secondary hover:text-dm-text-primary'

export const CopyIntoStreamDialog = ({
  kind,
  event,
  onCopied,
  onClose
}: {
  kind: CopyKind
  event: EventDetail
  onCopied: (row: ChatOverlaySummary | EmoteWallSummary | TickerBar) => void
  onClose: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { events } = useEventsList()
  const titleId = useId()
  const [source, setSource] = useState<CopySource>('quick')
  const [quickRows, setQuickRows] = useState<SourceRow[] | null>(null)
  const [fromEventId, setFromEventId] = useState<string>('')
  const [streamRows, setStreamRows] = useState<{ eventId: string, rows: SourceRow[] } | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const canUseStream = streamChannelsAvailable(event)
  const [channels, setChannels] = useState<CopyChannels>(defaultCopyChannels(event))
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const streams = useMemo(() => copySourceStreams(events, event.eventId), [events, event.eventId])

  useEffect(() => {
    if (source !== 'quick' || quickRows != null || auth0Token == null) return
    let cancelled = false
    setLoading(true)
    const load = async (): Promise<void> => {
      try {
        if (kind === 'chat') {
          const { chats } = await graphqlRequest<{ chats: ChatOverlaySummary[] }>(listMyChatOverlaysQuery, {}, auth0Token)
          if (!cancelled) setQuickRows(sortByName(chats.map(chatRowOf), (r) => r.name, (r) => r.id))
        } else if (kind === 'tickerbar') {
          const { tickerBars } = await graphqlRequest<{ tickerBars: TickerBarSummary[] }>(listMyTickerBarsQuery, {}, auth0Token)
          if (!cancelled) setQuickRows(sortByName(tickerBars.map(tickerBarRowOf), (r) => r.name, (r) => r.id))
        } else {
          const { walls } = await graphqlRequest<{ walls: EmoteWallSummary[] }>(listMyEmoteWallsQuery, {}, auth0Token)
          if (!cancelled) setQuickRows(sortByName(walls.map(wallRowOf), (r) => r.name, (r) => r.id))
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your Quick Overlays')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [source, quickRows, auth0Token, kind])

  useEffect(() => {
    if (source !== 'stream' || fromEventId === '' || auth0Token == null) return
    if (streamRows != null && streamRows.eventId === fromEventId) return
    let cancelled = false
    setLoading(true)
    const load = async (): Promise<void> => {
      try {
        const { event: other } = await graphqlRequest<{ event: EventDetail | null }>(getEventQuery, { eventId: fromEventId }, auth0Token)
        let rows: SourceRow[] = []
        if (other != null && kind === 'chat') rows = (other.chatOverlays ?? []).map(chatRowOf)
        if (other != null && kind === 'wall') rows = (other.emoteWalls ?? []).map(wallRowOf)
        if (other != null && kind === 'tickerbar') rows = (other.tickerBars ?? []).map(tickerBarRowOf)
        if (!cancelled) setStreamRows({ eventId: fromEventId, rows: sortByName(rows, (r) => r.name, (r) => r.id) })
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load that stream')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [source, fromEventId, streamRows, auth0Token, kind])

  const rows: SourceRow[] | null = source === 'quick'
    ? quickRows
    : (streamRows != null && streamRows.eventId === fromEventId ? streamRows.rows : null)

  const pickSource = (next: CopySource): void => {
    if (next === source) return
    setSource(next)
    setSelected(null)
    setError(null)
  }

  const handleCopy = async (): Promise<void> => {
    if (auth0Token == null || busy || selected == null) return
    if (source === 'stream' && fromEventId === '') return
    setBusy(true)
    setError(null)
    try {
      const variables = copyVariablesFor({
        kind,
        eventId: event.eventId,
        fromId: selected,
        fromEventId: source === 'stream' ? fromEventId : null,
        channels: canUseStream ? channels : 'MINE'
      })
      if (kind === 'chat') {
        const { chat } = await graphqlRequest<{ chat: ChatOverlaySummary }>(copyChatOverlayToEventMutation, variables, auth0Token)
        onCopied(chat)
      } else if (kind === 'tickerbar') {
        const { tickerBar } = await graphqlRequest<{ tickerBar: TickerBar }>(copyTickerBarToEventMutation, variables, auth0Token)
        onCopied(tickerBar)
      } else {
        const { wall } = await graphqlRequest<{ wall: EmoteWallSummary }>(copyEmoteWallToEventMutation, variables, auth0Token)
        onCopied(wall)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not copy into this stream')
    } finally {
      setBusy(false)
    }
  }

  let list: JSX.Element
  if (source === 'stream' && fromEventId === '') {
    list = <p className='text-[11px] text-dm-text-accent'>{streams.length === 0 ? 'You are not in any other stream.' : 'Pick a stream first.'}</p>
  } else if (rows == null && error != null && !loading) {
    list = <></>
  } else if (rows == null || loading) {
    list = (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  } else if (rows.length === 0) {
    list = <p className='text-[11px] text-dm-text-accent' data-testid='copy-dialog-none'>Nothing to copy here yet.</p>
  } else {
    list = (
      <ul className='max-h-56 space-y-1.5 overflow-y-auto' data-testid='copy-dialog-rows'>
        {rows.map((r) => (
          <li key={r.id}>
            <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
              <input
                type='radio'
                name={`${titleId}-row`}
                checked={selected === r.id}
                onChange={() => setSelected(r.id)}
                className='size-4 border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              <span className='min-w-0 flex-1 truncate text-dm-text-primary'>{r.name}</span>
              <span className='shrink-0 text-[11px] text-dm-text-accent'>{r.detail}</span>
            </label>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
      data-testid='copy-into-stream-dialog'
      tabIndex={-1}
      onKeyDown={(e) => confirmDialogKeyDown(e, onClose)}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        className='max-h-[90vh] w-full min-w-0 max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-dm-bg-secondary p-6 shadow-2xl'
      >
        <h2 id={titleId} className='text-lg font-semibold text-dm-text-primary'>{COPY_DIALOG_COPY.title}</h2>
        <p className='mt-2 text-sm text-dm-text-secondary'>{COPY_DIALOG_COPY.body}</p>

        <div className='mt-4 inline-flex flex-wrap rounded-md border border-white/10 p-0.5' role='group' aria-label='Copy from'>
          <button type='button' onClick={() => pickSource('quick')} aria-pressed={source === 'quick'} data-testid='copy-dialog-tab-quick' className={tabClass(source === 'quick')}>
            {COPY_DIALOG_COPY.tabQuick}
          </button>
          <button type='button' onClick={() => pickSource('stream')} aria-pressed={source === 'stream'} data-testid='copy-dialog-tab-stream' className={tabClass(source === 'stream')}>
            {COPY_DIALOG_COPY.tabStream}
          </button>
        </div>

        {source === 'stream' && (
          <select
            value={fromEventId}
            onChange={(e) => { setFromEventId(e.target.value); setSelected(null); setError(null) }}
            aria-label='Stream'
            data-testid='copy-dialog-stream-select'
            className='mt-3 w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
          >
            <option value=''>Pick a stream</option>
            {streams.map((e) => (
              <option key={e.eventId} value={e.eventId}>{e.name}</option>
            ))}
          </select>
        )}

        <div className='mt-3'>{list}</div>

        {copyKindHasChannels(kind) && (
          <fieldset className='mt-4 space-y-1.5'>
            <legend className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>Channels</legend>
            <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
              <input
                type='radio'
                name={`${titleId}-channels`}
                checked={canUseStream && channels === 'STREAM'}
                disabled={!canUseStream}
                onChange={() => setChannels('STREAM')}
                data-testid='copy-dialog-channels-stream'
                className='size-4 border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand disabled:opacity-50'
              />
              <span className={canUseStream ? '' : 'opacity-50'}>{COPY_DIALOG_COPY.useStream}</span>
            </label>
            <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
              <input
                type='radio'
                name={`${titleId}-channels`}
                checked={!canUseStream || channels === 'MINE'}
                onChange={() => setChannels('MINE')}
                data-testid='copy-dialog-channels-mine'
                className='size-4 border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              <span>{COPY_DIALOG_COPY.keepMine}</span>
            </label>
            <p className='text-[11px] text-dm-text-accent' data-testid='copy-dialog-hint'>{COPY_DIALOG_COPY.hint}</p>
          </fieldset>
        )}

        {error != null && (
          <div className='mt-3'>
            <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
          </div>
        )}

        <div className='mt-5 flex flex-wrap items-center justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            autoFocus
            className='rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={() => { void handleCopy() }}
            disabled={busy || selected == null}
            data-testid='copy-dialog-submit'
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy && <LoadingSpinner />}
            {COPY_DIALOG_COPY.button}
          </button>
        </div>
      </div>
    </div>
  )
}
